/**
 * @file index.tsx
 *
 * @fileoverview Renders desktop navigation sub menu.
 */
import {
  Root,
  Trigger,
  Content,
  Item,
  AccordionItemProps,
} from "@radix-ui/react-accordion";
import { cva, VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import ArrowDownIcon from "@jq-icons/ArrowDown";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import { AccordionProps } from "../../../02-atoms/25-accordion";
import styles from "./sideBlockSubMenu.module.scss";

interface SideBlockSubMenuProps
  extends Pick<AccordionProps, "multipleItems" | "children"> {}

export function SideBlockSubMenu({
  children,
  multipleItems = false,
  ...props
}: SideBlockSubMenuProps) {
  return (
    <Root
      type={multipleItems ? "multiple" : "single"}
      collapsible={true}
      {...props}
    >
      {children}
    </Root>
  );
}

const wrapper = cva([styles.wrapper], {
  variants: {
    collapsed: {
      true: styles.wrapperCollapsed,
    },
  },
  defaultVariants: {
    collapsed: false,
  },
});

interface SideBlockSubMenuItemProps
  extends AccordionItemProps,
    VariantProps<typeof wrapper> {
  /**
   * The menu label component.
   */
  Label: ReactNode;
  /**
   * The down icon title.
   *
   * @default 'down'
   */
  iconTitle?: string;
}

export function SideBlockSubMenuItem({
  children,
  Label,
  iconTitle,
  collapsed,
  className = "",
  ...props
}: SideBlockSubMenuItemProps) {
  return (
    <Item {...props} className={wrapper({ collapsed })}>
      <Trigger className={styles.subMenuTrigger}>
        <FlexItem className={styles.labelWrapper}>{Label}</FlexItem>
        <ArrowDownIcon
          color="gray7"
          title={iconTitle ?? "down"}
          className={styles.arrowIcon}
        />
      </Trigger>
      <div className={`${styles.subMenuContent}`}>
        <Content>
          <ul>{children}</ul>
        </Content>
      </div>
    </Item>
  );
}

const contentItemWrapper = cva([styles.contentItemWrapper], {
  variants: {
    hasIcon: {
      true: styles.contentItemWrapperWithIcon,
    },
    active: {
      true: styles.contentItemWrapperActive,
    },
  },
});

interface SideBlockSubMenuContentItemProps {
  /**
   * The displayed menu link component.
   */
  Label: ReactNode;
  /**
   * The icon.
   */
  Icon?: ReactNode;
  /**
   * The active flag.
   */
  active?: boolean;
}

export function SideBlockSubMenuContentItem({
  Label,
  Icon,
  active,
}: SideBlockSubMenuContentItemProps) {
  return (
    <li
      className={contentItemWrapper({
        hasIcon: !!Icon,
        active,
      })}
    >
      {Icon && Icon}
      <span>{Label}</span>
    </li>
  );
}
